

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useState } from 'react';
import { MotionConfig } from 'framer-motion';
import { AuthProvider } from '@/contexts/AuthContext';
import { EffectivePlanProvider } from '@/contexts/EffectivePlanContext';
import { ThemeProvider } from '@/components/theme-provider';
import { Toaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useReducedMotion } from '@/hooks/useReducedMotion';

export function Providers({ children }: { children: React.ReactNode }) {
  // Reader "Reduce motion" setting OR the OS preference — makes every
  // framer-motion `motion.*` component in the app skip its animation and jump
  // straight to the end state, wherever it's used (see globals.css's
  // [data-motion="reduced"] rule for the non-framer-motion CSS transitions).
  const { effective: reduceMotion } = useReducedMotion();
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // Stale time: data is considered fresh for 5 minutes
            staleTime: 5 * 60 * 1000,
            // Cache time: keep unused data in cache for 10 minutes
            gcTime: 10 * 60 * 1000,
            // Retry failed requests once
            retry: 1,
            // Keep focus refetch off globally to avoid network bursts on slow links.
            // Queries that truly need focus refresh should opt in individually.
            refetchOnWindowFocus: false,
            // Don't refetch on mount if data is fresh
            refetchOnMount: false,
          },
          mutations: {
            // Never retry by default: mutations are POST/PATCH/DELETE, and a
            // client-side timeout says nothing about whether the server applied
            // the first attempt — retrying double-submits. A mutation that is
            // genuinely idempotent can opt in per-call.
            retry: 0,
          },
        },
      })
  );

  return (
    <QueryClientProvider client={queryClient}>
      <MotionConfig reducedMotion={reduceMotion ? 'always' : 'user'}>
        <ThemeProvider
          attribute="class"
          defaultTheme="dark"
          enableSystem={false}
          disableTransitionOnChange
        >
          <AuthProvider>
            <EffectivePlanProvider>
              <TooltipProvider>
                {children}
              </TooltipProvider>
            </EffectivePlanProvider>
          </AuthProvider>
          <Toaster />
        </ThemeProvider>
      </MotionConfig>
    </QueryClientProvider>
  );
}
